import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Requisition } from '@/lib/schemas';

interface UseRequisitionsOptions {
  page?: number;
  limit?: number;
}

export function useRequisitions(options: UseRequisitionsOptions = {}) {
  const { page = 1, limit = 10 } = options;

  return useQuery<{data: Requisition[], totalCount: number}, Error>({
    queryKey: ['requisitions', page, limit],
    queryFn: () => api.getRequisitions({ page, limit }),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useRequisition(id: string) {
  return useQuery<Requisition | null, Error>({
    queryKey: ['requisitions', id],
    queryFn: () => api.getRequisition(id),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useCreateRequisition() {
  const queryClient = useQueryClient();
  return useMutation<Requisition, Error, Omit<Requisition, 'requisition_id' | 'requisition_number' | 'requested_at' | 'created_at' | 'updated_at'>>({
    mutationFn: (data) => api.createRequisition(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
    },
  });
}

export function useUpdateRequisition() {
  const queryClient = useQueryClient();
  return useMutation<Requisition | null, Error, { id: string; data: Partial<Omit<Requisition, 'requisition_id' | 'requisition_number' | 'requested_at' | 'created_at'>>}>({
    mutationFn: ({ id, data }) => api.updateRequisition(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
    },
  });
}

export function useDeleteRequisition() {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, string>({
    mutationFn: (id) => api.deleteRequisition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
    },
  });
}

export function useSubmitRequisitionForApproval() {
  const queryClient = useQueryClient();
  return useMutation<Requisition | null, Error, string>({
    mutationFn: (id) => api.submitRequisitionForApproval(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
    },
  });
}

export function useApproveRequisition() {
  const queryClient = useQueryClient();
  return useMutation<Requisition | null, Error, { id: string; approverId: string; comments?: string }>({
    mutationFn: ({ id, approverId, comments }) => api.approveRequisitionItem(id, approverId, comments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
    },
  });
}

export function useRejectRequisition() {
  const queryClient = useQueryClient();
  return useMutation<Requisition | null, Error, { id: string; approverId: string; comments: string }>({
    mutationFn: ({ id, approverId, comments }) => api.rejectRequisition(id, approverId, comments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
    },
  });
}

export function useRequestRevisionRequisition() {
  const queryClient = useQueryClient();
  return useMutation<Requisition | null, Error, { id: string; approverId: string; comments: string }>({
    mutationFn: ({ id, approverId, comments }) => api.requestRevisionRequisition(id, approverId, comments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
    },
  });
}

export function useDelegateApproval() {
  const queryClient = useQueryClient();
  return useMutation<Requisition | null, Error, { id: string; approverId: string; delegateToId: string }>({
    mutationFn: ({ id, approverId, delegateToId }) => api.delegateApproval(id, approverId, delegateToId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
    },
  });
}

